'use client'

import { useState, use, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import RichTextEditor from '@/components/RichTextEditor'
import { slugify } from '@/lib/slugify' // <-- ADDED THIS IMPORT

export default function NewThreadPage({ params }: { params: Promise<{ slug: string }> }) {
  const resolvedParams = use(params)
  // Extract the actual ID from the slug (e.g., "cm123-tech-talk" -> "cm123")
  const categoryId = resolvedParams.slug.split('-')[0]
  
  const router = useRouter()
  
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [tags, setTags] = useState('') 
  const [wantsPremium, setWantsPremium] = useState(false) 
  
  // Flair States
  const [flairs, setFlairs] = useState<any[]>([])
  const [selectedFlairId, setSelectedFlairId] = useState('')

  // Poll States
  const [showPollCreator, setShowPollCreator] = useState(false)
  const [pollQuestion, setPollQuestion] = useState('')
  const [pollOptions, setPollOptions] = useState(['', ''])

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Fetch flairs on mount using the extracted categoryId
  useEffect(() => {
    if (!categoryId) return
    fetch(`/api/categories/${categoryId}/flairs`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setFlairs(data)
      })
      .catch((err) => console.error('Failed to load category flairs', err))
  }, [categoryId])

  // Poll Handlers
  const handleAddOption = () => {
    if (pollOptions.length < 6) {
      setPollOptions([...pollOptions, ''])
    }
  }

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...pollOptions]
    newOptions[index] = value
    setPollOptions(newOptions)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const res = await fetch('/api/threads/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          content,
          categoryId: categoryId, // Uses extracted ID
          tags,
          wantsPremium,
          flairId: selectedFlairId || null,
          poll: showPollCreator && pollQuestion.trim() ? {
            question: pollQuestion,
            options: pollOptions.filter(o => o.trim() !== '')
          } : null
        })
      })

      const data = await res.json()

      if (!res.ok) throw new Error(data.error || 'Failed to create thread')

      // 🚀 Redirect straight to the newly created thread
      const threadSlug = slugify(title)
      router.push(`/thread/${data.threadId}-${threadSlug}`)
      router.refresh()
    } catch (err: any) {
      setError(err.message)
      setLoading(false)
    }
  }

  return (
    <main className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        {/* Back Link using the SEO slug */}
        <Link href={`/category/${resolvedParams.slug}`} className="text-sm font-medium text-indigo-400 hover:text-indigo-300 mb-6 inline-block">
          &larr; Back to Category
        </Link>

        <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-xl">
          <h1 className="text-xl font-bold text-slate-200 mb-6">Create New Thread</h1>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-xs rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Thread Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                maxLength={100}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-indigo-500"
                placeholder="What do you want to discuss?"
              />
            </div>
            
            {flairs.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Topic Flair</label>
                <select
                  value={selectedFlairId}
                  onChange={(e) => setSelectedFlairId(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-indigo-500"
                >
                  <option value="">Select a flair (optional)</option>
                  {flairs.map((flair) => (
                    <option key={flair.id} value={flair.id}>
                      {flair.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Tags (Comma Separated)</label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                maxLength={50}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-indigo-500"
                placeholder="e.g. hosting, servers, domains"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">Initial Post Content</label>
              <RichTextEditor 
                value={content} 
                onChange={setContent} 
                placeholder="Write your message here..."
              />
            </div>

            {/* --- POLL CREATOR SECTION --- */}
            <div className="bg-slate-950 border border-slate-800 p-4 rounded-xl space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider block">Attach a Poll</span>
                  <span className="text-[11px] text-slate-500">Allow members to vote on a question inside this thread.</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPollCreator(!showPollCreator)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                    showPollCreator ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {showPollCreator ? 'Remove Poll' : '+ Add Poll'}
                </button>
              </div>

              {showPollCreator && (
                <div className="space-y-3 pt-3 border-t border-slate-800">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Poll Question</label>
                    <input
                      type="text"
                      value={pollQuestion}
                      onChange={e => setPollQuestion(e.target.value)}
                      placeholder="e.g., Which framework do you use for backend APIs?"
                      className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase">Poll Options</label>
                    {pollOptions.map((opt, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={opt}
                          onChange={e => handleOptionChange(idx, e.target.value)}
                          placeholder={`Option ${idx + 1}`}
                          className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-indigo-500"
                        />
                      </div>
                    ))}
                    {pollOptions.length < 6 && (
                      <button
                        type="button"
                        onClick={handleAddOption}
                        className="text-xs text-indigo-400 font-bold hover:underline pt-1 block"
                      >
                        + Add another option
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* --- PREMIUM THREAD PROMOTION TOGGLE --- */}
            <div 
              onClick={() => setWantsPremium(!wantsPremium)}
              className={`p-4 rounded-xl border transition-all cursor-pointer select-none flex items-center justify-between gap-4 ${
                wantsPremium
                  ? 'bg-amber-950/20 border-amber-500/50 shadow-[0_0_15px_rgba(245,158,11,0.1)]'
                  : 'bg-slate-950 border-slate-800 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center gap-3.5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                  wantsPremium ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-800 text-slate-400'
                }`}>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-200">Highlight & Boost Thread</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                      -20 pts
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Gives your thread a glowing border and a Boosted badge on the homepage.
                  </p>
                </div>
              </div>

              <div className={`w-11 h-6 flex items-center rounded-full p-1 duration-300 ease-in-out shrink-0 ${
                wantsPremium ? 'bg-amber-500' : 'bg-slate-800'
              }`}>
                <div className={`bg-slate-950 w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${
                  wantsPremium ? 'translate-x-5' : ''
                }`} />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={loading}
                className="bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-2 px-6 rounded-lg text-sm transition-colors disabled:opacity-50 shadow-lg shadow-indigo-600/20"
              >
                {loading ? 'Posting...' : 'Post Thread'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  )
}